import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import beepSound from "../Sound/3beeps-108353.mp3";
import { Bell } from "lucide-react";

export const GlobalContext = createContext();

export const GlobalProvider = ({ children }) => {
  const navigate = useNavigate();

  // ========= ENV =========
  const baseUrl =
    process.env.NODE_ENV === "development"
      ? process.env.REACT_APP_SALES_API_BASE_URL
      : process.env.REACT_APP_SALES_API_BASE_URL;

  const baseUrl2 = process.env.REACT_APP_SALES_API_NEW_BASE_URL;
  const baseUrl_upload = process.env.REACT_APP_ANLOOK_API_BASE_URL_UPLOAD;
 

  const username = process.env.REACT_APP_API_AUTH_USERNAME;
  const password = process.env.REACT_APP_API_AUTH_PASSWORD;
  const appName = process.env.REACT_APP_APP_NAME;

  const username2= process.env.REACT_APP_SALES_API_AUTH_USERNAME
  const password2= process.env.REACT_APP_SALES_API_AUTH_PASSWORD

  const basicAuth = "Basic " + btoa(`${username}:${password}`);
  const basicAuth2 = "Basic " + btoa(`${username2}:${password2}`);

  const bumppyBaseUrl = process.env.REACT_APP_BUMPPY_API_BASE_URL;
  const bumppyUsername = process.env.REACT_APP_BUMPPY_API_AUTH_USERNAME;
  const bumppyPassword = process.env.REACT_APP_BUMPPY_API_AUTH_PASSWORD;

  const anlookBaseUrl = process.env.REACT_APP_ANLOOK_API_BASE_URL;
  const anlookUsername = process.env.REACT_APP_ANLOOK_API_AUTH_USERNAME;
  const anlookPassword = process.env.REACT_APP_ANLOOK_API_AUTH_PASSWORD;

  // ========= GLOBAL STATES =========
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);

  const [generatedHTML, setGeneratedHTML] = useState("");
  const userID =
    currentAdmin?.userid ||
    currentAdmin?.userId ||
    currentAdmin?.userID ||
    null;

  const [adminId, setAdminId] = useState("");
  const [AllClients, setAllClients] = useState([]);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [error, setError] = useState("");
  const [team, setTeam] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [GateWay, setGateWay] = useState([]);

  // ========= GLOBAL STATES =========

  // Dashboard states
  const [statsDash, setStatsDash] = useState(null);
  const [ordersSeries, setOrdersSeries] = useState([]);
  const [ordersMiniKPI, setOrdersMiniKPI] = useState(null);
  const [ordersMiniSeries, setOrdersMiniSeries] = useState([]);
  const [userAccess, setUserAccess] = useState([]);

  // WebSocket states
  const [wsConnected, setWsConnected] = useState(false);
  const [lastWsMessage, setLastWsMessage] = useState(null);
  const [ordersUpdateTick, setOrdersUpdateTick] = useState(0);

  // ========= SOUND STATE =========

  const [ordersSoundMuted, setOrdersSoundMuted] = useState(true);
  const orderSoundRef = useRef(null);
  const lastBeepAtRef = useRef(0);

  useEffect(() => {
    try {
      const a = new Audio(beepSound);
      a.volume = 1.0;
      orderSoundRef.current = a;
    } catch (e) {
      console.error("GlobalContext: failed to init order sound:", e);
    }

    return () => {
      if (orderSoundRef.current) {
        orderSoundRef.current.pause();
        orderSoundRef.current = null;
      }
    };
  }, []);

  const triggerOrderBeep = useCallback(
    async (from = "unknown") => {
      if (ordersSoundMuted) {
        return;
      }
      const audio = orderSoundRef.current;
      if (!audio) {
        return;
      }

      const now = Date.now();
      if (now - lastBeepAtRef.current < 2000) {
        return;
      }
      lastBeepAtRef.current = now;

      try {
        for (let i = 0; i < 2; i++) {
          audio.pause();
          audio.currentTime = 0;

          // eslint-disable-next-line no-await-in-loop
          await audio.play().catch((err) => {
            console.warn("GlobalContext: audio play blocked", err);
            throw err;
          });

          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => {
            const handler = () => {
              audio.removeEventListener("ended", handler);
              resolve();
            };
            audio.addEventListener("ended", handler);
          });
        }
      } catch (e) {
        console.error("GlobalContext: error while playing sound twice:", e);
      }
    },
    [ordersSoundMuted],
  );

  // ========= NOTIFICATION (TOAST) ON/OFF =========
  const [ordersNotificationsEnabled, setOrdersNotificationsEnabled] = useState(
    () => {
      try {
        const raw = localStorage.getItem("ordersNotificationsEnabled");
        return raw !== "0";
      } catch {
        return true;
      }
    },
  );

  useEffect(() => {
    try {
      localStorage.setItem(
        "ordersNotificationsEnabled",
        ordersNotificationsEnabled ? "1" : "0",
      );
    } catch {}
  }, [ordersNotificationsEnabled]);

  // ========= GLOBAL NEW-ORDER TRACKING =========
  const latestOrderIdRef = useRef(null);

  // ========= ADMIN HELPERS =========
  const normalizeAdminFromSession = (rawStr) => {
    try {
      const parsed = rawStr ? JSON.parse(rawStr) : null;
      if (!parsed) return { adminMeta: null, adminId: "" };
      const meta = parsed.adminMeta || parsed.user || parsed;
      const id =
        meta?.adminId ?? meta?.pkid ?? meta?.id ?? meta?.pk_admin_id ?? "";
      return { adminMeta: meta || null, adminId: id ? String(id) : "" };
    } catch {
      return { adminMeta: null, adminId: "" };
    }
  };

  const readAnyAdminFromStorage = useCallback(() => {
    const tryKeys = [
      { store: sessionStorage, key: "userData" },
      { store: localStorage, key: "userData" },
      { store: localStorage, key: "admin" },
      { store: localStorage, key: "currentAdmin" },
    ];
    for (const { store, key } of tryKeys) {
      try {
        const raw = store.getItem(key);
        if (!raw) continue;
        const { adminMeta, adminId: sid } = normalizeAdminFromSession(raw);
        if (adminMeta && sid) {
          return { adminMeta, adminId: sid };
        }
        if (adminMeta) return { adminMeta, adminId: "" };
      } catch {}
    }
    return { adminMeta: null, adminId: "" };
  }, []);

  const ensureAdmin = useCallback(() => {
    const { adminMeta, adminId: sid } = readAnyAdminFromStorage();
    if (adminMeta && !currentAdmin) setCurrentAdmin(adminMeta);
    if (sid && sid !== adminId) {
      setAdminId(String(sid));
    }
    return { adminMeta, adminId: sid };
  }, [readAnyAdminFromStorage, currentAdmin, adminId]);

  // ========= AXIOS INSTANCE =========

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: baseUrl,
      headers: {
        "Content-Type": "application/json",
        Authorization: basicAuth,
        "X-App-Name": appName || "WebApp",
      },
    });

    instance.interceptors.request.use((config) => {
      try {
        if (adminId) {
          config.headers["ADMIN-ID"] = String(adminId);
        } else {
          const { adminId: sid } = readAnyAdminFromStorage();
          if (sid) config.headers["ADMIN-ID"] = String(sid);
        }
      } catch (_) {}
      return config;
    });

    instance.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err?.response?.status === 401) {
          sessionStorage.removeItem("userData");
          toast.error("Session expired. Please log in again.");
          window.location.assign("/login");
        }
        return Promise.reject(err);
      },
    );

    return instance;
  }, [baseUrl, basicAuth, appName, adminId, readAnyAdminFromStorage]);

  const api2 = useMemo(() => {
    const instance = axios.create({
      baseURL: baseUrl2,
      headers: {
        "Content-Type": "application/json",
        Authorization: basicAuth,
        "X-App-Name": appName || "WebApp",
      },
    });

    instance.interceptors.request.use((config) => {
      try {
        if (adminId) {
          config.headers["ADMIN-ID"] = String(adminId);
        } else {
          const { adminId: sid } = readAnyAdminFromStorage();
          if (sid) config.headers["ADMIN-ID"] = String(sid);
        }
      } catch (_) {}
      return config;
    });

    instance.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err?.response?.status === 401) {
          sessionStorage.removeItem("userData");
          toast.error("Session expired. Please log in again.");
          window.location.assign("/login");
        }
        return Promise.reject(err);
      },
    );

    return instance;
  }, [baseUrl, basicAuth, appName, adminId, readAnyAdminFromStorage]);
  
const api3 = useMemo(() => {
  return axios.create({
    baseURL: baseUrl_upload,
    headers: {
      Authorization: basicAuth2,
      "x-api-key":"cd95d405291787dc15ed77fa7e5c91e64fcb708b375fc6f492kk"
    },
  });
}, [
  baseUrl_upload,
  basicAuth2,
  appName,
]);

  const authHeaders = useMemo(() => {
    const h = {
      "Content-Type": "application/json",
      Authorization: basicAuth,
      "X-App-Name": appName || "WebApp",
    };
    if (adminId) h["X-Admin-Id"] = String(adminId);
    else {
      const { adminId: sid } = readAnyAdminFromStorage();
      if (sid) h["X-Admin-Id"] = String(sid);
    }
    return h;
  }, [basicAuth, appName, adminId, readAnyAdminFromStorage]);

  const bumppyBasicAuth = useMemo(() => {
    return `Basic ${window.btoa(`${bumppyUsername}:${bumppyPassword}`)}`;
  }, [bumppyUsername, bumppyPassword]);

  const bumppyApi = useMemo(() => {
    const instance = axios.create({
      baseURL: bumppyBaseUrl,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: bumppyBasicAuth,
      },
    });

    return instance;
  }, [bumppyBaseUrl, bumppyBasicAuth]);

    const anlookBasicAuth = useMemo(() => {
      return `Basic ${window.btoa(`${anlookUsername}:${anlookPassword}`)}`;
    }, [anlookUsername, anlookPassword]);

    const anlookApi = useMemo(() => {
      const instance = axios.create({
        baseURL: anlookBaseUrl,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: anlookBasicAuth,
        },
      });

      return instance;
    }, [anlookBaseUrl, anlookBasicAuth]);

  const orderApi = useMemo(
    () => ({
      listOrders: "/Admin/Order/v1.0/getOrders",
      orderById: "/Admin/Order/v1.0/getOrderById",
      updateStatus: "/Admin/Order/v1.0/updateStatus",
      timeline: "/Admin/Order/v1.0/getTimeline",
      assignRider: "/Admin/Order/v1.0/assignRider",
      addNote: "/Admin/Order/v1.0/addNote",
      summary: "/Admin/Order/v1.0/getOrderSummary",
    }),
    [],
  );

  // ========= NEW ORDER TOAST HELPER =========
  const showNewOrderToast = useCallback(
    (orderId) => {
      if (!ordersNotificationsEnabled) return;

      toast.info(
        ({ closeToast }) => (
          <div className="flex items-start gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <Bell className="h-4 w-4" />
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <span className="text-xs font-semibold text-gray-900">
                  New order received
                </span>
                <span className="rounded-full bg-emerald-50 px-1.5 py-[2px] text-[9px] font-semibold uppercase tracking-wide text-emerald-700">
                  Live
                </span>
              </div>

              {orderId && (
                <span className="text-[10px] text-gray-600">
                  Order ID: <span className="font-medium">{orderId}</span>
                </span>
              )}

              <button
                className="mt-1 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-medium text-emerald-700 transition hover:bg-emerald-50"
                onClick={() => {
                  navigate("/admin/orders");
                  if (closeToast) closeToast();
                }}
              >
                Go to Orders →
              </button>
            </div>
          </div>
        ),
        {
          position: "top-right",
          autoClose: 5000,
          icon: false,
          className: "rounded-lg shadow-md p-3 w-[250px] min-h-[60px]",
          progressClassName: "bg-emerald-500",
        },
      );
    },
    [navigate, ordersNotificationsEnabled],
  );

  // ========= GLOBAL NEW ORDER CHECK (API BASED) =========
  const checkNewOrderAndBeep = useCallback(async () => {
    const sid = adminId || readAnyAdminFromStorage().adminId;
    if (!sid) {
      return;
    }

    try {
      const { data } = await api.post("/Order/v1.0/getAllOrderData", {
        adminId: String(sid),
      });

      if (!Array.isArray(data?.response) || data.response.length === 0) {
        return;
      }

      const list = data.response;
      const first = list[0];
      const currentId = first?.orderid || first?.orderId || null;

      if (!currentId) {
        return;
      }

      // 1st time
      if (!latestOrderIdRef.current) {
        latestOrderIdRef.current = currentId;
        if (!ordersSoundMuted) {
          await triggerOrderBeep("api_first");
        }
        return;
      }

      // New order
      if (latestOrderIdRef.current !== currentId) {
        latestOrderIdRef.current = currentId;
        if (!ordersSoundMuted) {
          await triggerOrderBeep("api_diff");
        }
      }
    } catch (e) {
      console.error("GlobalContext: checkNewOrderAndBeep error:", e);
    }
  }, [
    adminId,
    api,
    readAnyAdminFromStorage,
    triggerOrderBeep,
    ordersSoundMuted,
  ]);

  // ========= GEOLOCATION =========
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
        },
        (err) => {
          setError(err.message);
        },
      );
    } else {
      setError("Geolocation is not supported by this browser.");
    }
  }, []);

  // ========= INIT ADMIN =========
  useEffect(() => {
    const { adminMeta, adminId: sid } = readAnyAdminFromStorage();
    if (adminMeta) setCurrentAdmin(adminMeta);
    if (sid) setAdminId(String(sid));
  }, [readAnyAdminFromStorage]);

  // ========= DASHBOARD HELPERS =========
  const GetAllClientList = useCallback(
    async ({ limit = 1000, offset = 0 } = {}) => {
      const sid = adminId || readAnyAdminFromStorage().adminId;

      if (!sid) {
        setAllClients([]);
        return [];
      }

      setLoading(true);

      try {
        const payload = {
          userid: String(sid),
          latitude:
            latitude !== null && latitude !== undefined ? String(latitude) : "",
          longitude:
            longitude !== null && longitude !== undefined
              ? String(longitude)
              : "",
          limit: Number(limit),
          offset: Number(offset),
        };

        const { data } = await api.post(
          "/Admin/v3.0/getAllClientList",
          payload,
        );

        if (data?.responseCode === "00") {
          const clients = Array.isArray(data?.response) ? data.response : [];

          setAllClients(clients);
          return clients;
        }

        setAllClients([]);

        throw new Error(data?.responseMessage || "Failed to fetch clients");
      } catch (error) {
        console.error("GetAllClientList error:", error);

        setAllClients([]);

        if (error?.response?.status !== 401) {
          toast.error(
            error?.response?.data?.responseMessage ||
              error?.message ||
              "Failed to fetch clients.",
          );
        }

        return [];
      } finally {
        setLoading(false);
      }
    },
    [adminId, api, latitude, longitude, readAnyAdminFromStorage],
  );

  const pullDashboardAnalytics = useCallback(async () => {
    const sid = adminId || readAnyAdminFromStorage().adminId;
    if (!sid) return;
    try {
      const { data } = await api.post("/Admin/getDashboardAnalytics", {
        adminId: sid,
        userid: "string",
        interval: "30",
      });
      if (data?.responseCode === "00") {
        const s = data.response || {};
        setStatsDash(s);
        setOrdersSeries([
          { name: "Received", count: Number(s?.received_orders_count || 0) },
          {
            name: "Dispatched",
            count: Number(s?.dispatched_orders_count || 0),
          },
          { name: "Completed", count: Number(s?.completed_orders_count || 0) },
        ]);
      } else {
        setStatsDash(null);
        setOrdersSeries([]);
      }
    } catch {
      setStatsDash(null);
      setOrdersSeries([]);
    }
  }, [adminId, readAnyAdminFromStorage, api]);

  // const pullOrdersMini = useCallback(async () => {
  //   const sid = adminId || readAnyAdminFromStorage().adminId;
  //   if (!sid) return;
  //   try {
  //     const { data } = await api.post("/Admin/v1.0/getOrdersDashboard", {
  //       interval: 14,
  //       adminId: sid,
  //     });
  //     if (data?.responseCode === "00") {
  //       const r = data.response || {};
  //       setOrdersMiniKPI(r);
  //       setOrdersMiniSeries(
  //         (r.daily || []).map((d) => ({
  //           name: d.date,
  //           count: Number(d.count || 0),
  //         })),
  //       );
  //     } else {
  //       setOrdersMiniKPI(null);
  //       setOrdersMiniSeries([]);
  //     }
  //   } catch {
  //     setOrdersMiniKPI(null);
  //     setOrdersMiniSeries([]);
  //   }
  // }, [adminId, readAnyAdminFromStorage, api]);

  const refreshDashboardAnalytics = useCallback(async () => {
    await Promise.all([
      pullDashboardAnalytics(),
      // pullOrdersMini(),
      GetAllClientList({
        limit: 100,
        offset: 0,
      }),
    ]);

    toast.success("Dashboard refreshed");
  }, [pullDashboardAnalytics, GetAllClientList]);

  useEffect(() => {
    if (!adminId) return;

    const loadDashboardData = async () => {
      await Promise.all([
        GetAllClientList({
          limit: 1000,
          offset: 0,
        }),
        pullDashboardAnalytics(),
        // pullOrdersMini(),
      ]);
    };

    loadDashboardData();
  }, [adminId, GetAllClientList, pullDashboardAnalytics]);

  // ========= VISIBILITY: tab change pe admin ensure =========
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") ensureAdmin();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [ensureAdmin]);

  // ========= ADMIN ID SYNC =========
  useEffect(() => {
    const nextId =
      currentAdmin?.adminId ??
      currentAdmin?.pkid ??
      currentAdmin?.id ??
      currentAdmin?.pk_admin_id ??
      "";
    if (nextId && String(nextId) !== adminId) {
      setAdminId(String(nextId));
    }
  }, [currentAdmin, adminId]);

  // ========= GLOBAL WEBSOCKET =========
  useEffect(() => {
    const sid = adminId || readAnyAdminFromStorage().adminId;
    if (!sid) {
      return;
    }

    let socket = null;
    let reconnectTimer = null;

    const wsURL = `wss://api.anlook.com/ws?userId=${sid}&roomId=anlook_issues_v1`;

    const connectWebSocket = () => {
      if (
        socket &&
        (socket.readyState === WebSocket.OPEN ||
          socket.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }

      socket = new WebSocket(wsURL);
      socket.onopen = () => {
        setWsConnected(true);
      };

      socket.onmessage = (event) => {
        setLastWsMessage(event.data);
        setOrdersUpdateTick((prev) => prev + 1);
        pullDashboardAnalytics();
        // pullOrdersMini();

        try {
          const msgObj = JSON.parse(event.data);
          const text = String(
            msgObj.Message || msgObj.message || "",
          ).toLowerCase();

          if (text.includes("new order")) {
            if (!ordersSoundMuted) {
              triggerOrderBeep("ws_fallback");
            }
            showNewOrderToast(null);
          }
        } catch (e) {
          console.warn("GlobalContext: failed to parse WS message JSON:", e);
        }
        checkNewOrderAndBeep();
      };

      socket.onerror = (err) => {
        console.error(" GlobalContext: WebSocket error:", err);
      };

      socket.onclose = () => {
        console.warn(" GlobalContext: WebSocket closed");
        setWsConnected(false);

        if (!reconnectTimer) {
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            connectWebSocket();
          }, 5000);
        }
      };
    };
    connectWebSocket();
    return () => {
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        socket.close();
      }
      if (reconnectTimer) clearTimeout(reconnectTimer);
      setWsConnected(false);
    };
  }, [
    adminId,
    readAnyAdminFromStorage,
    pullDashboardAnalytics,
    // pullOrdersMini,
    checkNewOrderAndBeep,
    ordersSoundMuted,
    triggerOrderBeep,
    showNewOrderToast,
  ]);

  // ordersUpdateTick ab sirf consumers ke liye hai
  useEffect(() => {
    if (!adminId) return;
    if (!ordersUpdateTick) return;
  }, [ordersUpdateTick, adminId]);

  // Debug helper :
  useEffect(() => {
    window.testOrderBeep = () => {
      triggerOrderBeep("manual_test");
    };
  }, [triggerOrderBeep]);

  // ========= CONTEXT VALUE =========
  return (
    <GlobalContext.Provider
      value={{
        baseUrl,
        username,
        appName,
        api,
        api2,
        api3,
        bumppyApi,
        bumppyBaseUrl,
        bumppyBasicAuth,
        anlookApi,
        anlookBaseUrl,
        anlookBasicAuth,
        authHeaders,
        adminId,

        // websocket info
        wsConnected,
        lastWsMessage,
        ordersUpdateTick,

        // global sound
        ordersSoundMuted,
        setOrdersSoundMuted,
        triggerOrderBeep,

        //  notifications
        ordersNotificationsEnabled,
        setOrdersNotificationsEnabled,

        // shared data
        team,
        setTeam,
        searchQuery,
        setSearchQuery,
        loading,
        currentAdmin,
        setCurrentAdmin,
        AllClients,
        latitude,
        longitude,

        // dashboards
        statsDash,
        ordersSeries,
        ordersMiniKPI,
        ordersMiniSeries,
        orderApi,
        userAccess,
        setUserAccess,

        // actions
        GetAllClientList,
        pullDashboardAnalytics,
        // pullOrdersMini,
        refreshDashboardAnalytics,

        ensureAdmin,
        GateWay,
        setGateWay,
        selectedClient,
        setSelectedClient,

        // banner
        userID,
        generatedHTML,
        setGeneratedHTML,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};;